import { useEffect, useRef, useState } from 'react';
import { int, isoDay } from '../lib/format';
import { C, ink } from '../lib/theme';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import type { CrmClientRow } from '../lib/types';
import { ClientCard } from '../components/ClientCard';
import { MonthPicker } from '../components/MonthPicker';
import { IconAlert, IconCircleDashed, IconClock, IconUsers } from '../components/icons';
import { KpiCard } from '../components/KpiCard';
import { EmptyState, ErrorBanner, SectionHeader, Skeleton } from '../components/primitives';

const FINANCEIRO_PILLS: Array<{ id: 'em_dia' | 'pendencia' | 'falha' | undefined; label: string }> = [
  { id: undefined, label: 'Todos' },
  { id: 'falha', label: 'Inadimplentes' },
  { id: 'pendencia', label: 'Pendentes' },
  { id: 'em_dia', label: 'Adimplentes' },
];

function currentMonthRange(): { from: string; to: string } {
  const d = new Date();
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return { from: isoDay(first), to: isoDay(last) };
}

/**
 * Módulo Clientes (CRM) — só clientes com assinatura ativa (pedido explícito:
 * "eu só preciso dos clientes com assinaturas ativas"). O que importa aqui é
 * responder duas perguntas por cliente: quais produtos/zonas ele tem ativos,
 * e se está em dia com a cobrança — não é uma variação da Fila do módulo CS,
 * é uma pergunta de produto diferente, por isso o `ClientCard` é um
 * componente próprio, não um reaproveitamento do `EventCard`.
 */
export function ClientesPage({ onOpenClient }: {
  onOpenClient: (masterId: string) => void;
}) {
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [financeiro, setFinanceiro] = useState<'em_dia' | 'pendencia' | 'falha' | undefined>(undefined);
  // Sincroniza com o mês vigente por padrão (pedido explícito: "puxar o mês
  // vigente mas que possa ser editado") — calculado de `new Date()` a cada
  // carga da página, nunca hardcoded. Editável via MonthPicker (‹ › ou "Tudo").
  const [{ from, to }, setPeriod] = useState<{ from?: string; to?: string }>(() => currentMonthRange());

  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  const kpis = useAsync(() => api.crmKpis({ from, to }), [from, to]);

  const [items, setItems] = useState<CrmClientRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    const mine = ++seq.current;
    setLoading(true);
    setError(null);
    api.crmClients({ q: q || undefined, financeiro, from, to, limit: 50 })
      .then((r) => {
        if (seq.current !== mine) return;
        setItems(r.items);
        setCursor(r.next_cursor);
        setHasMore(r.next_cursor !== null);
      })
      .catch((err) => { if (seq.current === mine) setError(err.message ?? 'Falha ao carregar clientes.'); })
      .finally(() => { if (seq.current === mine) setLoading(false); });
  }, [q, financeiro, from, to]);

  function loadMore() {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    api.crmClients({ q: q || undefined, financeiro, from, to, limit: 50, cursor })
      .then((r) => {
        setItems((prev) => [...prev, ...r.items]);
        setCursor(r.next_cursor);
        setHasMore(r.next_cursor !== null);
      })
      .catch((err) => setError(err.message ?? 'Falha ao carregar mais clientes.'))
      .finally(() => setLoadingMore(false));
  }

  const k = kpis.data;

  return (
    <>
      <section style={{ marginBottom: 32 }}>
        <SectionHeader id="resumo-clientes" title="Clientes ativos" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 16, alignItems: 'start' }}>
          <KpiCard
            icon={<IconUsers />} label="Clientes ativos" loading={kpis.loading}
            formula="clientes com ao menos 1 assinatura status = active"
            value={int(k?.total_clientes)} format={(n) => n.toLocaleString('pt-BR')}
            color={C.fg} chipBg={ink(0.08)}
          />
          <KpiCard
            icon={<IconAlert />} label="Inadimplentes" loading={kpis.loading}
            formula="clientes ativos cuja fatura mais recente no período tem status_derivado = falhou"
            value={int(k?.clientes_com_falha)} format={(n) => n.toLocaleString('pt-BR')}
            color={C.churn} glow="0 0 26px rgba(242,85,90,0.32)" chipBg="rgba(242,85,90,0.16)"
          />
          <KpiCard
            icon={<IconClock />} label="Pendentes" loading={kpis.loading}
            formula="clientes ativos cuja fatura mais recente no período está pendente"
            value={int(k?.clientes_com_pendencia)} format={(n) => n.toLocaleString('pt-BR')}
            color={C.amber} glow="0 0 26px rgba(245,165,36,0.28)" chipBg="rgba(245,165,36,0.16)"
          />
        </div>
      </section>

      <section>
        <SectionHeader id="lista-clientes" title="Lista de clientes" />

        <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 18, flexWrap: 'wrap' }}>
          <input
            type="text" value={qInput} onChange={(e) => setQInput(e.target.value)}
            placeholder="Buscar por nome ou domínio…"
            style={{
              flex: '1 1 240px', minWidth: 200, font: 'inherit', fontSize: 13, padding: '10px 15px',
              borderRadius: 12, border: `1px solid ${ink(0.14)}`, background: 'var(--input-bg)',
              color: C.fg, outline: 'none',
            }}
          />

          <MonthPicker from={from} onChange={(f, t) => setPeriod({ from: f, to: t })} />

          <div style={{ display: 'flex', gap: 6 }}>
            {FINANCEIRO_PILLS.map((p) => (
              <button
                key={p.label} type="button" onClick={() => setFinanceiro(p.id)}
                style={{
                  border: 'none', cursor: 'pointer', font: 'inherit', fontSize: 12.5, fontWeight: 700,
                  padding: '9px 15px', borderRadius: 11, whiteSpace: 'nowrap',
                  background: financeiro === p.id ? `linear-gradient(150deg, ${C.accent}, ${C.accentDeep})` : ink(0.06),
                  color: financeiro === p.id ? '#fff' : ink(0.65),
                  boxShadow: financeiro === p.id ? '0 6px 16px rgba(232,85,48,0.3)' : 'none',
                }}
              >{p.label}</button>
            ))}
          </div>
        </div>

        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {Array.from({ length: 6 }, (_, i) => <Skeleton key={i} height={98} radius={18} />)}
          </div>
        ) : error ? (
          <ErrorBanner message={error} onRetry={() => setQ((v) => v)} />
        ) : items.length === 0 ? (
          <EmptyState
            tone="neutral" icon={<IconCircleDashed size={22} />} title="Nenhum cliente encontrado"
            text="Ajuste a busca ou o filtro de adimplência."
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {items.map((c) => (
              <ClientCard key={c.master_id} client={c} onOpen={() => onOpenClient(c.master_id)} />
            ))}

            {hasMore && (
              <div style={{ textAlign: 'center', padding: '14px 0' }}>
                <button
                  type="button" onClick={loadMore} disabled={loadingMore}
                  style={{
                    border: `1px solid ${ink(0.15)}`, background: ink(0.05), color: ink(0.75),
                    cursor: loadingMore ? 'wait' : 'pointer', font: 'inherit', fontSize: 12.5, fontWeight: 650,
                    padding: '9px 18px', borderRadius: 11,
                  }}
                >{loadingMore ? 'carregando…' : 'carregar mais 50'}</button>
              </div>
            )}
          </div>
        )}
      </section>
    </>
  );
}
